import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

export const EditorialPage = styled.main`
  min-height: 100vh;
  color: #0e1624;
`;

export const EditorialArchivePageShell = styled(EditorialPage)`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background-color: #efecf0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100vh;
    pointer-events: none;
    z-index: 0;
    background-image:
      linear-gradient(
        to bottom,
        rgba(239, 236, 240, 0) 40%,
        rgba(239, 236, 240, 0) 60%,
        rgba(239, 236, 240, 0.65) 80%,
        rgba(239, 236, 240, 1) 100%
      ),
      radial-gradient(
        circle at 50% 50%,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.45) 75%,
        rgba(0, 0, 0, 0.65) 100%
      ),
      linear-gradient(
        90deg,
        rgba(246, 245, 247, 0.98) 0%,
        rgba(246, 245, 247, 0.96) 34%,
        rgba(246, 245, 247, 0.78) 56%,
        rgba(246, 245, 247, 0.42) 74%,
        rgba(246, 245, 247, 0.16) 100%
      ),
      linear-gradient(180deg, #f6f5f7 0%, #eceaed 100%);
    background-repeat: no-repeat;
    background-position: center top;
    background-size: 100% 100%;
    -webkit-mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.96) 16%,
      rgba(0, 0, 0, 0.82) 28%,
      rgba(0, 0, 0, 0.56) 38%,
      rgba(0, 0, 0, 0.28) 45%,
      rgba(0, 0, 0, 0.1) 48%,
      rgba(0, 0, 0, 0.02) 50%,
      rgba(0, 0, 0, 0) 54%
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.96) 16%,
      rgba(0, 0, 0, 0.82) 28%,
      rgba(0, 0, 0, 0.56) 38%,
      rgba(0, 0, 0, 0.28) 45%,
      rgba(0, 0, 0, 0.1) 48%,
      rgba(0, 0, 0, 0.02) 50%,
      rgba(0, 0, 0, 0) 54%
    );
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

export const EditorialContainer = styled.div`
  width: min(1200px, calc(100% - 3rem));
  margin: 0 auto;

  @media (max-width: 720px) {
    width: min(100%, calc(100% - 1.5rem));
  }
`;

export const TopSpacer = styled.div`
  height: 120px;

  @media (max-width: 720px) {
    height: 96px;
  }
`;

export const IntroBand = styled.section`
  padding: 1.5rem 0 4rem;
  color: #f6f0e9;
`;

export const EditorialArchiveSection = styled.section`
  position: relative;
  isolation: isolate;
  overflow: clip;
  background: transparent;
  padding: 2.8rem 0 5rem;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(31, 41, 55, 0.8) 1px, transparent 1px),
      linear-gradient(90deg, rgba(31, 41, 55, 0.8) 1px, transparent 1px);
    background-size: 54px 54px;
    opacity: 0.1;
    pointer-events: none;
    z-index: 0;
    mask-image: radial-gradient(circle at 50% 36%, rgba(0, 0, 0, 0.92) 0%, rgba(0, 0, 0, 0.74) 42%, rgba(0, 0, 0, 0.28) 78%, transparent 100%);
  }
`;

export const Eyebrow = styled.p`
  margin: 0 0 0.9rem;
  font-size: 0.78rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(224, 161, 94, 0.88);
  font-weight: 700;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(2.8rem, 5.4vw, 5.8rem);
  line-height: 0.94;
  color: #fbf7f1;
  letter-spacing: -0.04em;
  max-width: 10ch;
`;

export const PageDeck = styled.p`
  margin: 1.4rem 0 0;
  max-width: 52rem;
  font-size: 1.05rem;
  line-height: 1.75;
  color: rgba(246, 240, 233, 0.82);
`;

export const ListingGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  gap: 1.25rem;
  padding: 0 0 4.5rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const StoryLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

type CardProps = {
  $surface: string;
  $glow: string;
};

export const StoryCard = styled.article<CardProps>`
  position: relative;
  min-height: 100%;
  border-radius: 28px;
  padding: 1.5rem;
  background: ${({ $surface }) => $surface};
  border: 1px solid rgba(12, 19, 32, 0.08);
  overflow: hidden;
  box-shadow: 0 24px 60px ${({ $glow }) => $glow};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
      linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px);
    background-size: 28px 28px;
    opacity: 0.12;
    pointer-events: none;
  }
`;

export const FeaturedCard = styled(StoryCard)`
  min-height: 520px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  @media (max-width: 980px) {
    min-height: 420px;
  }
`;

export const StoryMeta = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

export const StoryPill = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(14, 22, 36, 0.16);
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const StoryTitle = styled.h2`
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(1.8rem, 2.7vw, 3.4rem);
  line-height: 0.97;
  letter-spacing: -0.035em;
`;

export const CompactStoryTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(1.45rem, 2vw, 2rem);
  line-height: 1.02;
  letter-spacing: -0.03em;
`;

export const StorySummary = styled.p`
  position: relative;
  z-index: 1;
  margin: 1rem 0 0;
  font-size: 1rem;
  line-height: 1.72;
  max-width: 44rem;
`;

export const StoryFooter = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
  margin-top: 1.4rem;
  color: rgba(14, 22, 36, 0.72);
  font-size: 0.92rem;
  font-weight: 600;
`;

export const DetailWrap = styled.section`
  padding: 0 0 5rem;
`;

export const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.4rem;
  color: #0f1728;
  font-weight: 700;
  text-decoration: none;
`;

export const ArticleHero = styled.header<CardProps>`
  position: relative;
  border-radius: 30px;
  padding: 2rem;
  background: ${({ $surface }) => $surface};
  box-shadow: 0 24px 70px ${({ $glow }) => $glow};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
      linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px);
    background-size: 30px 30px;
    opacity: 0.12;
    pointer-events: none;
  }
`;

export const ArticleMetaRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.7rem;
  margin: 0 0 1.15rem;
`;

export const ArticlePill = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(14, 22, 36, 0.15);
  border-radius: 999px;
  padding: 0.4rem 0.8rem;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
`;

export const ShareButton = styled.button`
  border: 1px solid rgba(14, 22, 36, 0.18);
  background: rgba(255, 255, 255, 0.58);
  color: #0e1624;
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-weight: 800;
  cursor: pointer;
`;

export const ArticleTitle = styled.h1`
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(2.6rem, 4.9vw, 5rem);
  line-height: 0.94;
  letter-spacing: -0.04em;
  max-width: 12ch;
`;

export const ArticleDeck = styled.p`
  position: relative;
  z-index: 1;
  margin: 1.25rem 0 0;
  max-width: 48rem;
  font-size: 1.08rem;
  line-height: 1.78;
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.6rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  position: relative;
  z-index: 1;
  border-radius: 20px;
  padding: 1rem 1.1rem;
  background: rgba(255, 255, 255, 0.46);
  border: 1px solid rgba(14, 22, 36, 0.1);
`;

export const StatLabel = styled.p`
  margin: 0;
  font-size: 0.74rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 700;
  color: rgba(14, 22, 36, 0.68);
`;

export const StatValue = styled.p`
  margin: 0.45rem 0 0;
  font-size: 1.1rem;
  line-height: 1.35;
  font-weight: 800;
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 280px) minmax(0, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const StickyRail = styled.aside`
  align-self: start;
  position: sticky;
  top: 96px;
  display: grid;
  gap: 1rem;

  @media (max-width: 980px) {
    position: static;
  }
`;

export const RailCard = styled.section`
  border-radius: 24px;
  padding: 1.15rem;
  background: #fff;
  border: 1px solid rgba(14, 22, 36, 0.08);
  box-shadow: 0 18px 38px rgba(15, 23, 40, 0.07);
`;

export const RailTitle = styled.h2`
  margin: 0 0 0.9rem;
  font-size: 0.8rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 800;
  color: rgba(14, 22, 36, 0.56);
`;

export const RailList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.75rem;
`;

export const RailListItem = styled.li`
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(14, 22, 36, 0.08);
  font-weight: 700;
  line-height: 1.5;

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

export const BodyColumn = styled.article`
  display: grid;
  gap: 1.8rem;
`;

export const ProseSection = styled.section`
  border-radius: 26px;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(14, 22, 36, 0.08);
  box-shadow: 0 18px 38px rgba(15, 23, 40, 0.06);
`;

export const SectionTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: var(--font-serif);
  font-size: clamp(1.5rem, 2vw, 2rem);
  line-height: 1.05;
  letter-spacing: -0.03em;
`;

export const SectionParagraph = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.82;
  color: rgba(14, 22, 36, 0.86);

  & + & {
    margin-top: 1rem;
  }
`;

export const BulletList = styled.ul`
  margin: 1rem 0 0;
  padding-left: 1.2rem;
  display: grid;
  gap: 0.7rem;
`;

export const BulletItem = styled.li`
  font-size: 1rem;
  line-height: 1.7;
`;

export const PullQuote = styled.blockquote`
  margin: 0;
  border-radius: 28px;
  padding: 1.7rem 1.8rem;
  background: linear-gradient(135deg, #101a2b, #15233a);
  box-shadow: 0 24px 60px rgba(15, 23, 40, 0.16);
  color: #f4efe8;
`;

export const PullQuoteText = styled.p`
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(1.8rem, 3vw, 2.8rem);
  line-height: 1.06;
  letter-spacing: -0.03em;
`;

export const PullQuoteAttribution = styled.footer`
  margin-top: 1rem;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(244, 239, 232, 0.68);
  font-weight: 700;
`;

export const ServiceChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

export const ServiceChip = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.5rem 0.85rem;
  background: rgba(14, 22, 36, 0.06);
  border: 1px solid rgba(14, 22, 36, 0.08);
  font-size: 0.86rem;
  font-weight: 700;
`;

export function usePublicationMeta(title: string, description: string) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | CyBard`;
    const descriptionTag = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionTag?.getAttribute('content') ?? null;
    if (descriptionTag) {
      descriptionTag.setAttribute('content', description);
    }
    return () => {
      document.title = previousTitle;
      if (descriptionTag && previousDescription !== null) {
        descriptionTag.setAttribute('content', previousDescription);
      }
    };
  }, [description, title]);
}

export function useSharePage(shareTitle: string) {
  const [state, setState] = useState<'idle' | 'copied'>('idle');

  const share = useCallback(async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url });
        return;
      } catch {
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setState('copied');
      window.setTimeout(() => setState('idle'), 1800);
    } catch {
      setState('idle');
    }
  }, [shareTitle]);

  return { share, state };
}
